<template>
    <v-dialog v-model="dialog" max-width="520px" persistent transition="dialog-bottom-transition">
        <v-card class="quick-signup-card">
            <!-- Header with gradient -->
            <div class="signup-header">
                <v-layout align-center justify-space-between class="pa-4">
                    <div class="header-content">
                        <v-icon color="white" size="32" class="mr-2">account_circle</v-icon>
                        <div>
                            <h2 class="white--text mb-0">Quick Sign Up</h2>
                            <span class="white--text subtitle-2" style="opacity: 0.9;">Join us in less than 30 seconds</span>
                        </div>
                    </div>
                    <v-btn icon @click="closeModal" color="white" class="close-btn">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-layout>
            </div>

            <!-- Motivation message -->
            <v-card-text class="pt-4 pb-2">
                <v-alert 
                    type="info" 
                    color="teal lighten-5" 
                    class="mb-4 motivation-alert" 
                    border="left"
                    colored-border
                    elevation="2"
                >
                    <v-layout align-center>
                        <v-icon color="teal darken-2" class="mr-2">shopping_cart</v-icon>
                        <span class="black--text body-2">Create your account now to continue shopping and enjoy exclusive benefits!</span>
                    </v-layout>
                </v-alert>

                <!-- Simplified form -->
                <v-form ref="form" v-model="valid" @submit.prevent="createAccount">
                    <v-text-field
                        v-model="formData.fullnames"
                        :rules="nameRules"
                        label="Full Name"
                        prepend-inner-icon="person"
                        color="teal"
                        required
                        outlined
                        dense
                        class="mb-2"
                        @keyup.enter="focusNext('username')"
                    ></v-text-field>

                    <v-text-field
                        ref="username"
                        v-model="formData.username"
                        :rules="usernameRules"
                        label="Username"
                        prepend-inner-icon="account_circle"
                        color="teal"
                        required
                        outlined
                        dense
                        class="mb-2"
                        @keyup.enter="focusNext('phone')"
                    ></v-text-field>

                    <div class="phone-input-wrapper mb-2">
                        <input 
                            ref="phone"
                            id="quick-phone" 
                            type="tel" 
                            class="phone-input"
                            @click="clearPhoneField"
                            v-model="formData.phone_number"
                            placeholder="Enter Phone Number"
                        />
                    </div>

                    <v-text-field
                        ref="password"
                        v-model="formData.password"
                        :rules="passwordRules"
                        :type="showPassword ? 'text' : 'password'"
                        label="Password (min. 8 characters)"
                        prepend-inner-icon="lock"
                        :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                        @click:append="showPassword = !showPassword"
                        color="teal"
                        required
                        outlined
                        dense
                        counter
                        class="mb-2"
                        @keyup.enter="createAccount"
                    ></v-text-field>

                    <v-expand-transition>
                        <v-text-field
                            v-if="showReferralField"
                            v-model="formData.referal_code"
                            label="Referral Code (Optional)"
                            prepend-inner-icon="card_giftcard"
                            color="teal"
                            outlined
                            dense
                            class="mb-2"
                        ></v-text-field>
                    </v-expand-transition>

                    <v-btn 
                        text 
                        small 
                        color="teal" 
                        @click="showReferralField = !showReferralField"
                        class="mb-2"
                    >
                        <v-icon small class="mr-1">{{ showReferralField ? 'remove' : 'add' }}</v-icon>
                        {{ showReferralField ? 'Hide' : 'Have a' }} Referral Code
                    </v-btn>
                </v-form>

                <!-- Error message with animation -->
                <v-slide-y-transition>
                    <v-alert 
                        v-if="errorMessage" 
                        type="error" 
                        dismissible 
                        class="mt-2 error-alert"
                        @input="errorMessage = ''"
                    >
                        {{ errorMessage }}
                    </v-alert>
                </v-slide-y-transition>
            </v-card-text>

            <!-- Actions - SECTION CORRIGÉE -->
            <v-card-actions class="px-4 pb-4 pt-0 actions-section">
                <v-layout column class="actions-layout">
                    <!-- Bouton de soumission principal -->
                    <v-btn
                        :loading="loading"
                        :disabled="!valid || loading"
                        color="teal darken-2"
                        dark
                        large
                        block
                        @click="createAccount"
                        class="mb-3 signup-btn"
                        elevation="2"
                        type="submit"
                    >
                        <v-icon left>check_circle</v-icon>
                        {{ loading ? 'Creating Account...' : 'Create My Account' }}
                    </v-btn>
                    
                    <v-divider class="my-2"></v-divider>
                    
                    <!-- Lien vers login -->
                    <v-btn
                        text
                        color="teal darken-2"
                        block
                        @click="goToLogin"
                        class="login-link-btn"
                    >
                        Already have an account? <strong class="ml-1">Sign In</strong>
                    </v-btn>
                </v-layout>
            </v-card-actions>

            <!-- Progress indicator -->
            <v-progress-linear
                v-if="loading"
                indeterminate
                color="teal"
                class="ma-0"
            ></v-progress-linear>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios'

export default {
    name: 'QuickSignupModal',
    
    props: {
        value: {
            type: Boolean,
            default: false
        },
        redirectPath: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            dialog: false,
            valid: false,
            loading: false,
            showPassword: false,
            showReferralField: false,
            phoneInput: null,
            phoneInitialized: false,
            phoneCleared: false,
            referalFromURL: false,
            
            formData: {
                fullnames: '',
                username: '',
                phone_number: 'Enter Phone Number',
                password: '',
                referal_code: ''
            },

            nameRules: [
                v => !!v || 'Full name is required',
                v => (v && v.length >= 4) || 'Name must be at least 4 characters'
            ],

            usernameRules: [
                v => !!v || 'Username is required',
                v => (v && v.length >= 4) || 'Username must be at least 4 characters'
            ],

            passwordRules: [
                v => !!v || 'Password is required',
                v => (v && v.length >= 8) || 'Password must be at least 8 characters'
            ]
        }
    },

    watch: {
        value(newVal) {
            this.dialog = newVal
            if (newVal) {
                this.$nextTick(() => {
                    this.initPhoneInput()
                    this.checkURLReferral()
                })
            }
        },
        dialog(newVal) {
            this.$emit('input', newVal)
            if (!newVal) {
                this.resetForm()
            }
        }
    },

    mounted() {
        this.checkURLReferral()
    },

    methods: {
        checkURLReferral() {
            const urlParams = new URLSearchParams(window.location.search);
            const referral = urlParams.get('referral');
            const affiliateReferral = urlParams.get('affiliate_referral');
                        
            if (affiliateReferral) {
                this.formData.referal_code = affiliateReferral;
                this.referalFromURL = true;
                this.showReferralField = true;
            } 
            else if (referral) {
                this.formData.referal_code = referral;
                this.referalFromURL = true;
                this.showReferralField = true;
            }
        },

        initPhoneInput() {
            if (this.phoneInitialized) return
            
            this.$nextTick(() => {
                const phoneInputField = document.querySelector("#quick-phone")
                if (phoneInputField && window.intlTelInput) {
                    function getIp(callback) {
                        fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { 
                            headers: { 'Accept': 'application/json' }
                        })
                        .then((resp) => resp.json())
                        .catch(() => {
                            return { country: 'cm' }
                        })
                        .then((resp) => callback(resp.country))
                    }

                    this.phoneInput = window.intlTelInput(phoneInputField, {
                        initialCountry: "cm",
                        geoIpLookup: getIp,
                        preferredCountries: ["cm", "us", "uk", "ci", "cg", "cf", "td", "ng", "gq", "ga"],
                        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
                    })
                    this.phoneInitialized = true
                }
            })
        },

        clearPhoneField() {
            if (!this.phoneCleared && this.formData.phone_number === 'Enter Phone Number') {
                this.formData.phone_number = ''
                this.phoneCleared = true
            }
        },

        focusNext(refName) {
            if (this.$refs[refName]) {
                this.$refs[refName].focus()
            }
        },

        async createAccount() {
            if (!this.$refs.form.validate()) {
                return
            }

            // Validation manuelle comme dans code 1
            if (this.formData.fullnames.length < 4) {
                this.$store.commit('setSnackBarMessage', "Your name are too short, please input atleast 2 of your names")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                return
            }
            else if (this.formData.username.length < 4) {
                this.$store.commit('setSnackBarMessage', "User name is too short, user name needs to be at least 4 characters")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                return
            }
            else if (this.formData.password.length < 8) {
                this.$store.commit('setSnackBarMessage', "Password is too short, needs to be at least 8 characters")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                return
            }
            else if (!this.phoneInput || !this.phoneInput.isValidNumber()) {
                this.$store.commit('setSnackBarMessage', "Phone number is invalid. Please input a valid number")
                this.$store.commit('setSnackBarColor', "red darken-4")
                this.$store.commit('activateSnackBar', true)
                return
            }

            this.loading = true

            try {
                const phoneNumber = this.phoneInput.getNumber()

                // Vérifications comme dans code 1
                let referal_flag = true
                let contact_flag = false

                // Check referral code if provided - COMME CODE 1
                if (this.formData.referal_code.length > 0) {
                    await axios
                        .get('/api/v1/savings/check/partner/referal_code/'+this.formData.referal_code+'/')
                        .then(response => {
                            referal_flag = response.data
                        })
                        .catch(error => {
                            if (error.response){
                                this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                                this.$store.commit('setSnackBarColor', "red darken-4")
                                this.$store.commit('activateSnackBar', true)
                            }else{
                                this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                                this.$store.commit('setSnackBarColor', "red darken-4")
                                this.$store.commit('activateSnackBar', true)
                            }
                        })
                }

                // Check if phone number already exists - COMME CODE 1
                if (this.phoneInput.isValidNumber()) {
                    await axios
                        .get('/api/v1/savings/check/client/contact/'+phoneNumber+'/')
                        .then(response => {
                            contact_flag = response.data
                        })
                        .catch(error => {
                            if (error.response){
                                this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                                this.$store.commit('setSnackBarColor', "red darken-4")
                                this.$store.commit('activateSnackBar', true)
                            }else{
                                this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                                this.$store.commit('setSnackBarColor', "red darken-4")
                                this.$store.commit('activateSnackBar', true)
                            }
                        })
                }
                
                if(referal_flag && !contact_flag) {
                    // 🔥 CORRECTION : Créer FormData exactement comme code 1
                    const userFormData = new FormData()
                    userFormData.append('username', this.formData.username)
                    userFormData.append('password', this.formData.password)

                    // Create user - EXACTEMENT COMME CODE 1
                    await axios
                        .post('/api/v1/users/', userFormData)
                        .then(response => {
                            console.log(response.data)
                            
                            // Create full account - EXACTEMENT COMME CODE 1
                            const accountFormData = new FormData()
                            accountFormData.append('username', this.formData.username)
                            accountFormData.append('password', this.formData.password)
                            accountFormData.append('phone_number', phoneNumber)
                            accountFormData.append('fullnames', this.formData.fullnames)
                            accountFormData.append('referal_code', this.formData.referal_code)

                            axios
                                .post('/api/v1/home/create/account/', accountFormData)
                                .then(response => {
                                    console.log(response.data[0])
                                    this.$store.commit('setSnackBarMessage', response.data[0].text)
                                    this.$store.commit('setSnackBarColor', response.data[0].color)
                                    this.$store.commit('activateSnackBar', true)

                                    if (response.data[0].valid){
                                        // Auto login exactement comme code 1
                                        const loginFormData = new FormData()
                                        loginFormData.append('username', this.formData.username)
                                        loginFormData.append('password', this.formData.password)

                                        axios
                                            .post('/api/v1/token/login/', loginFormData)
                                            .then(response => {
                                                this.$store.state.username = this.formData.username
                                                const token = response.data.auth_token
                                                this.$store.commit('setToken', token)
                                                axios.defaults.headers.common['Authorization'] = "Token " + token
                                                localStorage.setItem('token', token)
                                                
                                                // Success message
                                                this.$store.commit('setSnackBarMessage', '🎉 Account created successfully! Redirecting...')
                                                this.$store.commit('setSnackBarColor', 'green darken-2')
                                                this.$store.commit('activateSnackBar', true)

                                                // Redirection vers la destination prévue
                                                setTimeout(() => {
                                                    this.dialog = false
                                                    this.$router.push(this.redirectPath)
                                                }, 1500)
                                            })
                                            .catch(error => {
                                                if (error.response){
                                                    for (const property in error.response.data){
                                                        this.$store.commit('setSnackBarMessage', error.response.data[property][0])
                                                        this.$store.commit('setSnackBarColor', "red darken-4")
                                                        this.$store.commit('activateSnackBar', true)
                                                    }
                                                }else{
                                                    this.$store.commit('setSnackBarMessage', "An error occured while logging into your account")
                                                    this.$store.commit('setSnackBarColor', "red darken-4")
                                                    this.$store.commit('activateSnackBar', true)
                                                }
                                            })
                                    }
                                })
                                .catch(error => {
                                    if (error.response){
                                        this.$store.commit('setSnackBarMessage', error.response.status + " " + error.response.statusText)
                                        this.$store.commit('setSnackBarColor', "red darken-4")
                                        this.$store.commit('activateSnackBar', true)
                                    }else{
                                        this.$store.commit('setSnackBarMessage', "An error occured, please check your internet connection and try again later.")
                                        this.$store.commit('setSnackBarColor', "red darken-4")
                                        this.$store.commit('activateSnackBar', true)
                                    }
                                })
                        })
                        .catch(error => {
                            if (error.response){
                                for (const property in error.response.data){
                                    this.$store.commit('setSnackBarMessage', error.response.data[property][0])
                                    this.$store.commit('setSnackBarColor', "red darken-4")
                                    this.$store.commit('activateSnackBar', true)
                                }
                            }else{
                                this.$store.commit('setSnackBarMessage', "An error occured while logging into your account")
                                this.$store.commit('setSnackBarColor', "red darken-4")
                                this.$store.commit('activateSnackBar', true)
                            }
                        })
                }
                else {
                    if(!referal_flag){
                        this.$store.commit('setSnackBarMessage', "Referal code is invalid, please either you correct it or allow it blank")
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }
                    else if(contact_flag){
                        this.$store.commit('setSnackBarMessage', "This phone number has already been used to create an account, please use another number")
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }
                }

            } catch (error) {
                console.error('Signup error:', error)
                if (error.response && error.response.data) {
                    const errorData = error.response.data
                    if (typeof errorData === 'object') {
                        for (const property in errorData) {
                            this.$store.commit('setSnackBarMessage', errorData[property][0] || 'An error occurred during registration')
                            this.$store.commit('setSnackBarColor', "red darken-4")
                            this.$store.commit('activateSnackBar', true)
                            break
                        }
                    } else {
                        this.$store.commit('setSnackBarMessage', 'An error occurred. Please try again.')
                        this.$store.commit('setSnackBarColor', "red darken-4")
                        this.$store.commit('activateSnackBar', true)
                    }
                } else {
                    this.$store.commit('setSnackBarMessage', 'Connection error. Please check your internet and try again.')
                    this.$store.commit('setSnackBarColor', "red darken-4")
                    this.$store.commit('activateSnackBar', true)
                }
            } finally {
                this.loading = false
            }
        },

        goToLogin() {
            this.dialog = false
            this.$router.push('/login')
        },

        closeModal() {
            if (!this.loading) {
                this.dialog = false
            }
        },

        resetForm() {
            this.formData = {
                fullnames: '',
                username: '',
                phone_number: 'Enter Phone Number',
                password: '',
                referal_code: ''
            }
            this.showReferralField = false
            this.phoneCleared = false
            if (this.$refs.form) {
                this.$refs.form.resetValidation()
            }
        }
    }
}
</script>
<style scoped>
.quick-signup-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.signup-header {
    background: linear-gradient(135deg, #00897b 0%, #00695c 100%);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
}

.header-content {
    display: flex;
    align-items: center;
}

.close-btn {
    transition: transform 0.2s ease;
}

.close-btn:hover {
    transform: rotate(90deg);
}

.motivation-alert {
    border-radius: 8px;
    animation: slideInDown 0.4s ease;
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.phone-input-wrapper {
    position: relative;
}

.phone-input {
    width: 100%;
    padding: 12px 14px 12px 52px;
    border: 1px solid rgba(0, 0, 0, 0.38);
    border-radius: 4px;
    font-size: 16px;
    transition: all 0.3s ease;
}

.phone-input:focus {
    outline: none;
    border-color: #00897b;
    border-width: 2px;
    padding: 12px 14px 12px 51px;
}

.phone-input:hover {
    border-color: rgba(0, 0, 0, 0.87);
}

/* CORRECTIONS POUR LA SECTION DES BOUTONS */
.actions-section {
    background: white;
    position: relative;
    z-index: 10;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.actions-layout {
    width: 100%;
}

.signup-btn {
    font-weight: 600;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    min-height: 48px;
    margin-bottom: 12px !important;
}

.signup-btn:hover:not(.v-btn--disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 137, 123, 0.4);
}

.signup-btn:active {
    transform: translateY(0);
}

.login-link-btn {
    transition: all 0.2s ease;
    min-height: 40px;
}

.login-link-btn:hover {
    background-color: rgba(0, 137, 123, 0.08);
    transform: translateY(-1px);
}

.error-alert {
    animation: shake 0.5s ease;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
}

/* Vuetify overrides */
.v-text-field--outlined  fieldset {
    border-color: rgba(0, 0, 0, 0.38);
}

.v-text-field--outlined:hover fieldset {
    border-color: rgba(0, 0, 0, 0.87);
}

.v-text-field .v-input__slot {
    transition: all 0.3s ease;
}

.v-input--is-focused .v-input__slot {
    box-shadow: 0 0 0 2px rgba(0, 137, 123, 0.1);
}

/* Amélioration de la visibilité du bouton désactivé */
.v-btn--disabled {
    opacity: 0.6 !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
    .quick-signup-card {
        border-radius: 0;
        margin: 0;
    }
    
    .signup-header {
        border-radius: 0;
    }
    
    .actions-section {
        padding-left: 16px !important;
        padding-right: 16px !important;
        padding-bottom: 16px !important;
    }
    
    .signup-btn {
        min-height: 52px;
        font-size: 1rem;
    }
}

/* Assurer que le bouton est toujours visible */
.v-dialog {
    overflow-y: auto;
}

.v-card {
    display: flex;
    flex-direction: column;
}

.v-card__text {
    flex: 1;
    overflow-y: auto;
    max-height: 60vh;
}

.v-card__actions {
    flex-shrink: 0;
}
</style>